import { Linkedin, Twitter, Link as LinkIcon, Share2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/utils';

interface ShareButtonsProps {
  url: string;
  title: string;
  className?: string;
}

export function ShareButtons({ url, title, className }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  const onShare = async () => {
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({ title, url });
      } catch {
        // user cancelled — no-op
      }
    } else {
      onCopy();
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const twitterHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
  const linkedInHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <a
        href={twitterHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on X"
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-fg-muted transition-colors hover:border-gold-500/40 hover:text-gold-500"
      >
        <Twitter className="h-4 w-4" />
      </a>
      <a
        href={linkedInHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on LinkedIn"
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-fg-muted transition-colors hover:border-gold-500/40 hover:text-gold-500"
      >
        <Linkedin className="h-4 w-4" />
      </a>
      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy link"
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-fg-muted transition-colors hover:border-gold-500/40 hover:text-gold-500"
      >
        <LinkIcon className="h-4 w-4" />
        <span className="sr-only">{copied ? 'Copied' : 'Copy link'}</span>
      </button>
      <button
        type="button"
        onClick={onShare}
        aria-label="Share"
        className="inline-flex h-9 items-center gap-1 rounded-md border border-border px-3 text-sm text-fg-muted transition-colors hover:border-gold-500/40 hover:text-gold-500"
      >
        <Share2 className="h-4 w-4" />
        Share
      </button>
    </div>
  );
}